import type { SequenceTime } from "framer-motion";
import { CheckCheck } from "lucide-react";
import stepList from "../../assets/landing/step-list.webp";
import stepChoose from "../../assets/landing/step-choose.webp";
import stepSend from "../../assets/landing/step-send.webp";
import { cn } from "../../lib/cn";
import { Sparkle } from "../decor";
import { Scene } from "./stage/Scene";
import { SplitWords } from "./stage/Split";
import * as fx from "./stage/fx";
import type { DomStep, Timeline } from "./stage/choreography";

/* `pad` tints each stop along the route's sky → indigo → violet ink (literal classes for the JIT). */
const STEPS = [
  {
    img: stepList,
    title: "Make the list",
    body: "Add each child's essentials once. Update them anytime — every link stays current.",
    pad: "to-sky-50 ring-sky-100",
  },
  {
    img: stepChoose,
    title: "Choose who sees what",
    body: "Name the caregiver, pick the sections, and set how long the link lasts.",
    pad: "to-indigo-50 ring-indigo-100",
  },
  {
    img: stepSend,
    title: "Send the link",
    body: "Text it, email it, or show a QR code. They tap it and see the care sheet — no account needed.",
    pad: "to-violet-50 ring-violet-100",
  },
] as const;

/**
 * The desktop route: one gentle wave through the three stops. The columns
 * have no gap, so the stops sit at 1/6, 1/2 and 5/6 of the row at any width.
 * It sets out from an origin dot at (10, 96); leg i ends at stop i, so each
 * step's leg draws "to" it.
 */
const LEGS = [
  "M10 96 C 64 96, 104 60, 166.7 60",
  "M166.7 60 C 260 -2, 406.7 -2, 500 60",
  "M500 60 C 593.3 122, 740 122, 833.3 60",
];

/** On a phone the route runs down the left edge, one colour per leg (the desktop gradient's stops). */
const RAIL_COLORS = ["#38bdf8", "#6366f1", "#8b5cf6"];

/** A stop's object drops onto the route and settles with a small bounce. */
function drop(target: string, at: SequenceTime): DomStep {
  return {
    kind: "dom",
    target,
    keyframes: { opacity: [0, 1], y: [-56, 0] },
    options: { type: "spring", duration: 0.6, bounce: 0.35, at, opacity: fx.opacityTween(0.18) },
  };
}

/*
 * Signature: "Path draw". The headline stamps down word by word, then the
 * route sets out from its origin dot and draws itself stop by stop: the leg
 * arrives, the number badge pops, the object drops onto the line, its text
 * fades up. `<+x` starts a step x seconds after the previous one STARTED,
 * which keeps the stops evenly paced whatever each transition's length.
 *
 * The desktop curve and the phone rail share their names (origin, leg-i);
 * the engine plays only the one rendered at the current breakpoint.
 */
const TIMELINE: Timeline = [
  fx.fade("[data-fx=eyebrow]", { duration: 0.35 }),
  fx.stamp("[data-fx=title] [data-word]", { stagger: 0.09, at: "-0.2" }),
  fx.pop("[data-fx=origin]", { from: 0, at: "-0.2" }),
  ...STEPS.flatMap((_, i) => [
    fx.draw(`[data-fx=leg-${i}]`, { duration: i === 0 ? 0.3 : 0.36, at: i === 0 ? "<+0.1" : "<+0.14" }),
    fx.pop(`[data-fx=badge-${i}]`, { at: "<+0.26" }),
    drop(`[data-fx=stop-${i}]`, "<+0.08"),
    fx.fade(`[data-fx=text-${i}]`, { stagger: 0.06, at: "<+0.12" }),
  ]),
  fx.pop("[data-fx=chip]", { from: 0.6, at: "<+0.32" }),
];

/** One leg of the phone route: a vertical line through the stops' centres (x = 40px). */
function Rail({ leg, className }: { leg: number; className: string }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 4 100"
      preserveAspectRatio="none"
      fill="none"
      className={cn("pointer-events-none absolute left-10 w-1 -translate-x-1/2 overflow-visible md:hidden", className)}
    >
      <line
        data-fx={`leg-${leg}`}
        x1="2"
        y1="0"
        x2="2"
        y2="100"
        stroke={RAIL_COLORS[leg]}
        strokeWidth={3}
        strokeLinecap="round"
      />
    </svg>
  );
}

export function HowItWorks() {
  return (
    <Scene
      id="how"
      label="How it works"
      timeline={TIMELINE}
      className="bg-gradient-to-b from-indigo-50 via-sky-50 to-white"
    >
      {/* Backdrop: periwinkle wash, soft colour fields and a whisper of grain. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -left-40 top-[6%] h-[28rem] w-[28rem] rounded-full bg-indigo-200/45 blur-3xl" />
        <div className="absolute -right-32 top-1/3 h-[26rem] w-[26rem] rounded-full bg-sky-200/50 blur-3xl" />
        <div className="absolute -bottom-32 left-1/3 h-80 w-80 rounded-full bg-violet-200/35 blur-3xl" />
        <Sparkle className="absolute right-[9%] top-[16%] hidden h-5 w-5 text-indigo-300 sm:block" />
        <Sparkle className="absolute bottom-[13%] left-[6%] hidden h-4 w-4 text-sky-300 sm:block" />
        <div className="bg-grain absolute inset-0 opacity-[0.05] mix-blend-multiply" />
      </div>

      <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col justify-center px-6 pb-10 pt-20">
        <div className="md:text-center">
          <p data-fx="eyebrow" className="text-sm font-semibold uppercase tracking-wide text-indigo-700">
            How it works
          </p>
          <h2
            data-fx="title"
            className="mt-3 text-balance font-display text-3xl font-bold leading-[1.1] tracking-tight text-neutral-900 sm:text-4xl lg:text-5xl"
          >
            <SplitWords text="Set up once. Share in seconds." />
          </h2>
        </div>

        <div className="relative mt-10 md:mt-14 lg:mt-16">
          {/* Desktop route, behind the stops; its centre line runs through their centres. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-16 hidden -translate-y-1/2 md:block lg:top-[5.5rem]"
          >
            <svg viewBox="0 0 1000 120" fill="none" className="block h-auto w-full overflow-visible">
              <defs>
                <linearGradient id="how-route" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="1000" y2="0">
                  <stop offset="0" stopColor="#38bdf8" />
                  <stop offset="0.5" stopColor="#6366f1" />
                  <stop offset="1" stopColor="#8b5cf6" />
                </linearGradient>
              </defs>
              {LEGS.map((d, i) => (
                <path
                  key={i}
                  data-fx={`leg-${i}`}
                  d={d}
                  stroke="url(#how-route)"
                  strokeWidth={3}
                  strokeLinecap="round"
                />
              ))}
              <circle data-fx="origin" cx="10" cy="96" r="6" fill="#38bdf8" stroke="#ffffff" strokeWidth={3} />
            </svg>
          </div>

          <ol role="list" className="grid gap-y-5 md:grid-cols-3">
            {STEPS.map((step, i) => (
              <li
                key={step.title}
                className="relative grid grid-cols-[5rem_1fr] items-start gap-x-4 md:flex md:flex-col md:items-center md:px-3 md:text-center"
              >
                {/* Phone route: the origin and lead-in to the first stop, then each leg on to the next. */}
                {i === 0 && (
                  <>
                    <Rail leg={0} className="-top-4 h-14" />
                    <span aria-hidden="true" className="absolute -top-4 left-10 -translate-x-1/2 -translate-y-1/2 md:hidden">
                      <span data-fx="origin" className="block size-3.5 rounded-full bg-sky-400 ring-[3px] ring-white" />
                    </span>
                  </>
                )}
                {i < STEPS.length - 1 && <Rail leg={i + 1} className="-bottom-[3.75rem] top-10" />}

                <div className="relative size-20 md:size-32 lg:size-44">
                  <div
                    data-fx={`stop-${i}`}
                    className={cn(
                      "size-full rounded-full bg-gradient-to-b from-white p-[9%] shadow-soft ring-1",
                      step.pad,
                    )}
                  >
                    <img
                      src={step.img}
                      alt=""
                      aria-hidden="true"
                      width={320}
                      height={320}
                      loading="lazy"
                      draggable={false}
                      className="size-full select-none object-contain"
                    />
                  </div>
                  <span
                    data-fx={`badge-${i}`}
                    aria-hidden="true"
                    className="absolute -left-1 -top-1 flex size-7 items-center justify-center rounded-full bg-gradient-to-br from-sky-500 to-indigo-600 font-display text-sm font-bold text-white shadow-soft ring-[3px] ring-white md:left-0 md:top-0 md:size-10 md:text-lg"
                  >
                    {i + 1}
                  </span>
                </div>

                <div className="pt-1 md:mt-5 md:pt-0">
                  <h3
                    data-fx={`text-${i}`}
                    className="font-display text-base font-semibold text-neutral-900 md:text-xl"
                  >
                    {step.title}
                  </h3>
                  <p
                    data-fx={`text-${i}`}
                    className="mt-1 text-pretty text-sm leading-relaxed text-neutral-600 md:mx-auto md:mt-2 md:max-w-[17rem] md:text-[15px]"
                  >
                    {step.body}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className="mt-8 flex md:mt-12 md:justify-center">
          <p
            data-fx="chip"
            className="inline-flex items-center gap-3 rounded-2xl border border-indigo-100 bg-white/90 py-2 pl-2 pr-4 text-sm font-medium text-neutral-700 shadow-soft backdrop-blur sm:rounded-full"
          >
            <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <CheckCheck className="size-4" aria-hidden="true" />
            </span>
            You'll see when they open it — and when they confirm they've read it.
          </p>
        </div>
      </div>
    </Scene>
  );
}
